/**
 * Step 7: Upload Results
 * Shows the final results of the upload operation
 */

import {
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Button,
  Result,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons'
import type { UploadResult, LocalDbResult } from '../types'

const { Text } = Typography

interface UploadResultsStepProps {
  uploadResults: UploadResult[]
  localDbResults?: LocalDbResult[]
  onClose: () => void
}

export default function UploadResultsStep({
  uploadResults,
  localDbResults = [],
  onClose,
}: UploadResultsStepProps) {
  const newlyAddedCount = uploadResults.filter((r) => r.success && !r.is_duplicate).length
  const duplicateCount = uploadResults.filter((r) => r.success && r.is_duplicate).length
  const failCount = uploadResults.filter((r) => !r.success).length
  const localDbCount = localDbResults.length
  const hasVdlResults = uploadResults.length > 0
  const hasLocalResults = localDbCount > 0

  const columns: ColumnsType<UploadResult> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Домен',
      dataIndex: 'domain',
      key: 'domain',
      width: '25%',
      ellipsis: true,
      render: (domain: string) => (
        <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', fontWeight: 600, fontSize: 13 }}>
          {domain}
        </a>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 130,
      render: (_: any, record: UploadResult) => {
        if (!record.success) return <Tag icon={<CloseCircleOutlined />} color="error">Ошибка</Tag>
        if (record.is_duplicate) return <Tag icon={<WarningOutlined />} color="warning">Дубликат</Tag>
        return <Tag icon={<CheckCircleOutlined />} color="success">Загружен</Tag>
      },
    },
    {
      title: 'Детали',
      key: 'details',
      ellipsis: true,
      render: (_: any, record: UploadResult) => {
        // Show error message for failed uploads
        if (!record.success && record.error) {
          return (
            <Tooltip title={record.error} placement="topLeft">
              <Text type="danger" style={{ fontSize: 12 }}>
                <CloseCircleOutlined style={{ marginRight: 4 }} />
                {record.error.length > 60 ? `${record.error.substring(0, 60)}...` : record.error}
              </Text>
            </Tooltip>
          )
        }

        // Show success message
        if (record.success && record.is_duplicate) {
          return (
            <Text type="warning" style={{ fontSize: 12 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              Домен уже существует в VDL
            </Text>
          )
        }

        if (record.success && record.vdl_id) {
          return (
            <Text type="success" style={{ fontSize: 12 }}>
              <CheckCircleOutlined style={{ marginRight: 4 }} />
              Успешно добавлен в VDL
            </Text>
          )
        }

        return <Text type="secondary">—</Text>
      },
    },
    {
      title: 'VDL ID',
      dataIndex: 'vdl_id',
      key: 'vdl_id',
      width: 100,
      align: 'center' as const,
      render: (id?: number) => (id ? <Tag color="blue">{id}</Tag> : <Text type="secondary">—</Text>),
    },
  ]

  // Build subtitle based on what was uploaded
  const buildSubTitle = () => {
    const parts: string[] = []
    if (hasVdlResults) {
      parts.push(`VDL: ${newlyAddedCount} загружено`)
      if (duplicateCount > 0) parts.push(`${duplicateCount} дубликатов`)
      if (failCount > 0) parts.push(`${failCount} ошибок`)
    }
    if (hasLocalResults) {
      parts.push(`БД: ${localDbCount} страниц`)
    }
    return parts.join(' | ')
  }

  // Determine overall status
  const getResultStatus = () => {
    if (failCount > 0) return 'warning'
    if (newlyAddedCount === 0 && localDbCount === 0) return 'info'
    return 'success'
  }

  const getResultTitle = () => {
    if (!hasVdlResults && hasLocalResults) {
      return 'Все страницы сохранены в локальную БД!'
    }
    if (failCount === 0) {
      return hasLocalResults
        ? 'Все источники успешно обработаны!'
        : 'Все домены успешно загружены!'
    }
    return 'Загрузка завершена с ошибками'
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Result
        status={getResultStatus()}
        title={getResultTitle()}
        subTitle={buildSubTitle()}
        extra={[
          <Button type="primary" key="close" onClick={onClose}>
            Закрыть
          </Button>,
        ]}
      />

      <Row gutter={16}>
        {hasVdlResults && (
          <>
            <Col span={hasLocalResults ? 6 : 8}>
              <Card>
                <Statistic
                  title={<span><CloudUploadOutlined style={{ marginRight: 4 }} />VDL: Загружено</span>}
                  value={newlyAddedCount}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={hasLocalResults ? 6 : 8}>
              <Card>
                <Statistic title="Дубликаты" value={duplicateCount} valueStyle={{ color: '#fa8c16' }} prefix={<WarningOutlined />} />
              </Card>
            </Col>
            <Col span={hasLocalResults ? 6 : 8}>
              <Card>
                <Statistic title="Ошибок" value={failCount} valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} />
              </Card>
            </Col>
          </>
        )}
        {hasLocalResults && (
          <Col span={hasVdlResults ? 6 : 24}>
            <Card>
              <Statistic
                title={<span><DatabaseOutlined style={{ marginRight: 4 }} />БД: Страницы</span>}
                value={localDbCount}
                valueStyle={{ color: '#722ed1' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
        )}
      </Row>

      {/* Show error summary if there are failures */}
      {failCount > 0 && (
        <Alert
          message={`Не удалось загрузить ${failCount} ${failCount === 1 ? 'домен' : 'доменов'}`}
          description={
            <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
              <Text strong>Причины ошибок:</Text>
              {uploadResults
                .filter((r) => !r.success)
                .slice(0, 5)
                .map((r, idx) => (
                  <div key={idx} style={{
                    padding: '8px 12px',
                    background: '#fff1f0',
                    borderRadius: 4,
                    borderLeft: '3px solid #ff4d4f'
                  }}>
                    <Space direction="vertical" size={2}>
                      <Text strong style={{ fontSize: 13 }}>
                        <CloseCircleOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                        {r.domain}
                      </Text>
                      <Text type="danger" style={{ fontSize: 12, marginLeft: 22 }}>
                        {r.error || 'Неизвестная ошибка'}
                      </Text>
                    </Space>
                  </div>
                ))}
              {failCount > 5 && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                  ... и ещё {failCount - 5} {failCount - 5 === 1 ? 'ошибка' : 'ошибок'}
                </Text>
              )}
            </Space>
          }
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* VDL Results Table */}
      {hasVdlResults && (
        <Card
          title={
            <Space>
              <CloudUploadOutlined style={{ color: '#1890ff' }} />
              <span>Домены в VDL</span>
              <Tag color="blue">{uploadResults.length}</Tag>
            </Space>
          }
          size="small"
        >
          <Table
            dataSource={uploadResults}
            columns={columns}
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['15', '30', '50', '100'],
              showTotal: (total) => `Всего: ${total}`,
            }}
            size="small"
            rowKey="domain"
          />
        </Card>
      )}

      {/* Local DB Results Table */}
      {hasLocalResults && (
        <Card
          title={
            <Space>
              <DatabaseOutlined style={{ color: '#722ed1' }} />
              <span>Страницы в локальной БД</span>
              <Tag color="purple">{localDbCount}</Tag>
            </Space>
          }
          size="small"
        >
          <Table
            dataSource={localDbResults}
            columns={[
              {
                title: '#',
                key: 'index',
                width: 50,
                render: (_: any, __: any, index: number) => index + 1,
              },
              {
                title: 'URL',
                dataIndex: 'url',
                key: 'url',
                ellipsis: true,
                render: (url: string) => (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#722ed1', fontWeight: 500, fontSize: 13 }}
                  >
                    {url}
                  </a>
                ),
              },
              {
                title: 'Домен',
                dataIndex: 'host',
                key: 'host',
                width: 180,
                ellipsis: true,
                render: (host: string) => <Text style={{ fontSize: 13 }}>{host}</Text>,
              },
              {
                title: 'Статус',
                key: 'status',
                width: 120,
                render: (_: any, record: LocalDbResult) => (
                  record.success
                    ? <Tag icon={<CheckCircleOutlined />} color="purple">Сохранено</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">Ошибка</Tag>
                ),
              },
              {
                title: 'ID',
                dataIndex: 'parsed_url_id',
                key: 'parsed_url_id',
                width: 80,
                align: 'center' as const,
                render: (id?: number) => (id ? <Tag color="purple">{id}</Tag> : <Text type="secondary">—</Text>),
              },
            ]}
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['15', '30', '50', '100'],
              showTotal: (total) => `Всего: ${total}`,
            }}
            size="small"
            rowKey="url"
          />
        </Card>
      )}
    </Space>
  )
}
